import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { reportsApi, tasksApi, voipApi, queueApi } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import { useIsMobile } from '../../hooks/useIsMobile'
import { usePipelineStages, useScoringSegments } from '../../hooks/usePipelineStages'
import { Users, Target, TrendingUp, CheckSquare, Phone, ArrowRight, DollarSign, PhoneCall, RefreshCw, ClipboardList, Building2, Mail, ListChecks, HelpCircle, Zap } from 'lucide-react'
import { fmtWeekday, fmtDayMonth } from '../../utils/date'
import MobileHomePage from './MobileHomePage'
import LoadingState from '../../components/ui/LoadingState'

function StatCard({ icon: Icon, label, value, sub, color }: {
  icon: any; label: string; value: string | number; sub?: string; color: string
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 flex flex-col">
      <div className="flex items-center gap-2 mb-3 h-6">
        <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon size={13} className="text-white" />
        </div>
        <span className="text-xs text-gray-500 leading-tight">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-900 text-center">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1 text-center">{sub}</p>}
    </div>
  )
}

const FALLBACK_STAGE_COLORS: Record<string, string> = {
  new: '#3b82f6',
  contacted: '#0ea5e9',
  qualified: '#6366f1',
  demo_scheduled: '#8b5cf6',
  proposal_sent: '#a855f7',
  negotiation: '#d946ef',
  won: '#10b981',
}


function formatStage(stage: string): string {
  return stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function fmtINR(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function getGreeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

export default function DashboardPage() {
  const authUser = useAuthStore((s) => s.user)
  const isAdminOrManager = authUser?.role === 'admin' || authUser?.role === 'manager'
  const firstName = (authUser?.full_name || authUser?.username || '').split(' ')[0]

  // Mobile: show focused home screen instead of full dashboard
  const isMobile = useIsMobile()
  const stagesFromApi = usePipelineStages()
  const scoringSegments = useScoringSegments()
  const stageColors: Record<string, string> = stagesFromApi.length > 0
    ? Object.fromEntries(stagesFromApi.map(s => [s.slug, s.color || '#9ca3af']))
    : FALLBACK_STAGE_COLORS

  const FALLBACK_SEGMENTS = [
    { key: 'HOT', label: 'Hot', color: '#ef4444', min_score: 75 },
    { key: 'WARM', label: 'Warm', color: '#f59e0b', min_score: 45 },
    { key: 'COLD', label: 'Cold', color: '#3b82f6', min_score: 0 },
  ]
  const segmentConfig = scoringSegments.length > 0
    ? scoringSegments.map((s, i) => ({
        key: s.segment,
        label: s.label || s.segment,
        color: s.color || FALLBACK_SEGMENTS[i]?.color || '#9ca3af',
        min_score: s.min_score,
      }))
    : FALLBACK_SEGMENTS
  // All hooks MUST be called before any conditional returns (React rules of hooks)
  const showAdminDashboard = !isMobile && authUser?.role !== 'sales_rep' && authUser?.role !== 'marketing'

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'last_30_days'],
    queryFn: () => reportsApi.dashboard('last_30_days'),
    enabled: showAdminDashboard,
  })

  const { data: todayCosts } = useQuery({
    queryKey: ['voip-costs', 'today'],
    queryFn: () => reportsApi.voipCosts('today'),
    enabled: showAdminDashboard && isAdminOrManager,
  })

  const { data: monthCosts } = useQuery({
    queryKey: ['voip-costs', 'this_month'],
    queryFn: () => reportsApi.voipCosts('this_month'),
    enabled: showAdminDashboard && isAdminOrManager,
  })

  const { data: queueStatsData } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => queueApi.stats(),
    enabled: showAdminDashboard,
  })

  if (isMobile) {
    return <MobileHomePage />
  }
  if (authUser?.role === 'sales_rep') {
    return <SalesRepHome name={firstName} />
  }
  if (authUser?.role === 'marketing') {
    return <MarketingHome name={firstName} />
  }

  if (isLoading) {
    return <LoadingState message="Loading dashboard..." />
  }

  const d = data?.data as any || {}
  const pipeline = d.pipeline || {}
  const scores = d.score_distribution || {}
  const revenue = d.revenue || {}
  const activity = d.activity || {}
  const conversions = pipeline.conversion_rates || {}
  const funnel: { stage: string; count: number }[] = pipeline.funnel || []
  const maxCount = Math.max(...funnel.map((f: any) => f.count), 1)

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, {firstName}</h1>
        <p className="text-sm text-gray-500 mt-0.5">Here's your pipeline overview for the last 30 days</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <StatCard icon={Target} label="Total Leads" value={pipeline.total_leads || 0} color="bg-blue-500" />
        <StatCard icon={TrendingUp} label="Win Rate" value={`${pipeline.win_rate || 0}%`} color="bg-green-500" />
        <StatCard icon={Users} label="Won Deals" value={pipeline.won || 0} color="bg-emerald-500" />
        <StatCard icon={DollarSign} label="Revenue" value={`₹${(revenue.total_revenue || 0).toLocaleString()}`} color="bg-purple-500" />
        <StatCard icon={CheckSquare} label="Activities" value={activity.total_activities || 0} sub="Last 30 days" color="bg-amber-500" />
        <StatCard icon={Phone} label={`${segmentConfig[0]?.label || 'Hot'} Leads`} value={scores.segments?.[segmentConfig[0]?.key] || 0} color="bg-red-500" />
      </div>

      {/* Call Outcomes Today */}
      {(() => {
        const qs = (queueStatsData as any)?.data || {}
        const outcomes = qs.outcomes_today || {}
        const callsToday = qs.calls_today || 0
        if (!callsToday) return null
        const TIERS = [
          { key: 'interacted', label: 'Interacted', color: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50' },
          { key: 'connected', label: 'Connected', color: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50' },
          { key: 'answered', label: 'Answered', color: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50' },
          { key: 'no_answer', label: 'No Answer', color: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-50' },
        ]
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-indigo-500" />
                <h2 className="text-sm font-semibold text-gray-900">Today's Calls</h2>
              </div>
              <span className="text-xs text-gray-400">{callsToday} outbound</span>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {TIERS.map(({ key, label, text, bg }) => (
                <div key={key} className={`rounded-lg p-3 ${bg} text-center`}>
                  <p className={`text-2xl font-bold ${text}`}>{outcomes[key] || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
            {/* Progress bar */}
            <div className="flex h-2 rounded-full overflow-hidden mt-4 bg-gray-100">
              {TIERS.map(({ key, color }) => {
                const pct = callsToday > 0 ? ((outcomes[key] || 0) / callsToday) * 100 : 0
                return pct > 0 ? <div key={key} className={`${color}`} style={{ width: `${pct}%` }} /> : null
              })}
            </div>
          </div>
        )
      })()}

      {/* Pipeline Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pipeline</h2>
            <p className="text-xs text-gray-400 mt-0.5">{pipeline.total_leads || 0} leads across {funnel.length} stages</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-gray-600">Won: <span className="font-semibold text-emerald-700">{pipeline.won || 0}</span></span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-gray-600">Lost: <span className="font-semibold text-red-700">{pipeline.lost || 0}</span></span>
            </div>
          </div>
        </div>

        {/* Funnel visualization */}
        <div className="space-y-3 mb-6">
          {funnel.map((s: any) => {
            const pct = maxCount > 0 ? (s.count / maxCount) * 100 : 0
            const color = stageColors[s.stage] || '#9ca3af'
            return (
              <div key={s.stage} className="flex items-center gap-3">
                <div className="w-20 sm:w-28 text-right">
                  <span className="text-xs font-medium text-gray-600">{formatStage(s.stage)}</span>
                </div>
                <div className="flex-1 h-8 bg-gray-50 rounded-lg overflow-hidden">
                  <div
                    className="h-full rounded-lg flex items-center px-3 transition-all duration-500"
                    style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: color }}
                  >
                    {pct > 15 && <span className="text-xs font-bold text-white">{s.count}</span>}
                  </div>
                </div>
                {pct <= 15 && <span className="text-xs font-semibold text-gray-700 w-8">{s.count}</span>}
              </div>
            )
          })}
        </div>

        {/* Conversion rates */}
        {Object.keys(conversions).length > 0 && (
          <div className="pt-5 border-t border-gray-100">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Conversion Rates</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {Object.entries(conversions).map(([key, val]: [string, any]) => {
                const parts = key.split('_to_')
                const from = formatStage(parts[0] || '')
                const to = formatStage(parts[1] || '')
                const rateColor = val.rate > 50 ? 'text-green-600' : val.rate > 25 ? 'text-amber-600' : 'text-red-600'
                const bgColor = val.rate > 50 ? 'bg-green-50 border-green-200' : val.rate > 25 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
                return (
                  <div key={key} className={`rounded-lg border p-3 ${bgColor}`}>
                    <p className={`text-xl font-bold ${rateColor}`}>{val.rate}%</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-xs text-gray-500 truncate">{from}</span>
                      <ArrowRight size={12} className="text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-500 truncate">{to}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Call Spend — admin/manager only */}
      {isAdminOrManager && (() => {
        const todayS = (todayCosts?.data as any)?.summary
        const monthS = (monthCosts?.data as any)?.summary
        const topAgents: any[] = ((monthCosts?.data as any)?.by_agent || []).slice(0, 5)
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-teal-500 flex items-center justify-center">
                  <PhoneCall size={13} className="text-white" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Call Spend</h2>
              </div>
              <BackfillButton />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-5">
              <StatCard
                icon={PhoneCall}
                label="Call Spend (Today)"
                value={todayS ? fmtINR(todayS.grand_total) : '—'}
                sub={todayS ? `${todayS.total_calls} calls` : undefined}
                color="bg-teal-500"
              />
              <StatCard
                icon={DollarSign}
                label="Call Spend (This Month)"
                value={monthS ? fmtINR(monthS.grand_total) : '—'}
                sub={monthS ? `${monthS.billable_calls} billable of ${monthS.total_calls}` : undefined}
                color="bg-cyan-500"
              />
              <StatCard
                icon={TrendingUp}
                label="Avg Cost / Call"
                value={monthS ? fmtINR(monthS.avg_cost_per_call) : '—'}
                sub="This month"
                color="bg-sky-500"
              />
            </div>

            {topAgents.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Top Agents by Spend</h3>
                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {topAgents.map((a: any) => (
                    <div key={a.user_id} className="flex items-center justify-between py-2.5 border-b border-gray-50">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.user_name}</p>
                        <p className="text-xs text-gray-500">{a.calls} calls &middot; {fmtINR(a.avg_cost)}/call</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{fmtINR(a.total)}</p>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                        <th className="pb-2 font-medium">Agent</th>
                        <th className="pb-2 font-medium text-right">Calls</th>
                        <th className="pb-2 font-medium text-right">Total</th>
                        <th className="pb-2 font-medium text-right">Avg / Call</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topAgents.map((a: any) => (
                        <tr key={a.user_id} className="border-b border-gray-50">
                          <td className="py-2 text-gray-700 font-medium">{a.user_name}</td>
                          <td className="py-2 text-gray-600 text-right">{a.calls}</td>
                          <td className="py-2 text-gray-900 font-semibold text-right">{fmtINR(a.total)}</td>
                          <td className="py-2 text-gray-600 text-right">{fmtINR(a.avg_cost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Quality */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Lead Quality</h2>

          {/* Score gauge */}
          <div className="flex items-center justify-center mb-6">
            <div className="relative w-32 h-32">
              {/* Background ring */}
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#f3f4f6" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="42" fill="none"
                  stroke={
                    (scores.avg_score || 0) >= 75 ? '#ef4444' :
                    (scores.avg_score || 0) >= 45 ? '#f59e0b' : '#3b82f6'
                  }
                  strokeWidth="8"
                  strokeDasharray={`${((scores.avg_score || 0) / 100) * 264} 264`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{scores.avg_score ?? '—'}</span>
                <span className="text-[10px] text-gray-400">Avg Score</span>
              </div>
            </div>
          </div>

          {/* Segment bars */}
          <div className="space-y-3">
            {segmentConfig.map((seg, idx) => {
              const nextMin = idx > 0 ? segmentConfig[idx - 1].min_score - 1 : 100
              const range = `${seg.min_score}-${nextMin}`
              const count = scores.segments?.[seg.key] || 0
              const pct = scores.segment_percentages?.[seg.key] || 0
              return (
                <div key={seg.key}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: seg.color }} />
                      <span className="text-sm font-medium text-gray-700">{seg.label}</span>
                      <span className="text-[10px] text-gray-400">({range} pts)</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900">{count} <span className="font-normal text-gray-400 text-xs">({pct}%)</span></span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: seg.color }} />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Score distribution — horizontal stacked bar + breakdown */}
          {scores.histogram && (
            <div className="mt-6 pt-5 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Score Distribution</h4>

              {/* Stacked horizontal bar */}
              <div className="h-5 flex rounded-full overflow-hidden mb-4">
                {([
                  { key: '0-20', color: 'bg-blue-400', label: '0-20' },
                  { key: '21-40', color: 'bg-sky-400', label: '21-40' },
                  { key: '41-60', color: 'bg-yellow-400', label: '41-60' },
                  { key: '61-80', color: 'bg-amber-400', label: '61-80' },
                  { key: '81-100', color: 'bg-red-400', label: '81-100' },
                ]).map((b) => {
                  const count = (scores.histogram[b.key] as number) || 0
                  const total = Object.values(scores.histogram).reduce((a: number, c) => a + (c as number), 0) as number
                  const pct = total > 0 ? (count / total) * 100 : 0
                  return pct > 0 ? (
                    <div
                      key={b.key}
                      className={`${b.color} transition-all duration-500 relative group`}
                      style={{ width: `${pct}%` }}
                      title={`${b.label}: ${count} leads (${Math.round(pct)}%)`}
                    />
                  ) : null
                })}
              </div>

              {/* Legend rows */}
              <div className="space-y-2">
                {([
                  { key: '0-20', color: 'bg-blue-400', label: '0 – 20', desc: 'Very Cold' },
                  { key: '21-40', color: 'bg-sky-400', label: '21 – 40', desc: 'Cold' },
                  { key: '41-60', color: 'bg-yellow-400', label: '41 – 60', desc: 'Warm' },
                  { key: '61-80', color: 'bg-amber-400', label: '61 – 80', desc: 'Hot' },
                  { key: '81-100', color: 'bg-red-400', label: '81 – 100', desc: 'Very Hot' },
                ]).map((b) => {
                  const count = (scores.histogram[b.key] as number) || 0
                  return (
                    <div key={b.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-sm ${b.color}`} />
                        <span className="text-xs text-gray-600">{b.label}</span>
                        <span className="text-[11px] text-gray-400">{b.desc}</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-800">{count}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Recent Activity</h2>

          {/* Activity by type */}
          {activity.by_action && Object.keys(activity.by_action).length > 0 ? (
            <div className="space-y-2.5 mb-5">
              {Object.entries(activity.by_action)
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 8)
                .map(([action, count]) => {
                  const maxA = Math.max(...Object.values(activity.by_action).map(Number), 1)
                  const pct = ((count as number) / maxA) * 100
                  return (
                    <div key={action}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-gray-600 capitalize">{action.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-semibold text-gray-900">{count as number}</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full">
                        <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">No activity in this period</p>
          )}

          {/* Daily activity — heatmap style grid */}
          {activity.by_date && Object.keys(activity.by_date).length > 0 && (
            <div className="pt-5 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Daily Activity</h4>
              <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                {Object.entries(activity.by_date).slice(-14).map(([date, count]) => {
                  const maxD = Math.max(...Object.values(activity.by_date).slice(-14).map(Number), 1)
                  const intensity = maxD > 0 ? (count as number) / maxD : 0
                  const isToday = date === new Date().toISOString().slice(0, 10)
                  const dayLabel = fmtWeekday(date + 'T00:00:00')
                  const dateLabel = fmtDayMonth(date + 'T00:00:00')
                  const bg = (count as number) === 0 ? 'bg-gray-50' :
                             intensity > 0.75 ? 'bg-emerald-500' :
                             intensity > 0.5 ? 'bg-emerald-400' :
                             intensity > 0.25 ? 'bg-emerald-300' : 'bg-emerald-200'
                  return (
                    <div
                      key={date}
                      className={`rounded-lg p-1.5 sm:p-2.5 text-center ${bg} ${isToday ? 'ring-2 ring-indigo-500 ring-offset-1' : ''}`}
                      title={`${date}: ${count} activities`}
                    >
                      <p className={`text-[9px] sm:text-[10px] font-medium ${(count as number) > 0 && intensity > 0.5 ? 'text-white' : 'text-gray-500'}`}>{dayLabel}</p>
                      <p className={`text-base sm:text-lg font-bold ${(count as number) > 0 && intensity > 0.5 ? 'text-white' : 'text-gray-800'}`}>{count as number}</p>
                      <p className={`text-[8px] sm:text-[9px] ${(count as number) > 0 && intensity > 0.5 ? 'text-white/70' : 'text-gray-400'}`}>{dateLabel}</p>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Revenue summary */}
          {revenue.total_revenue > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-gray-900">₹{revenue.total_revenue.toLocaleString()}</p>
                  <p className="text-[10px] text-gray-400">Total Revenue</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{revenue.deal_count}</p>
                  <p className="text-[10px] text-gray-400">Deals Won</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">₹{(revenue.avg_deal_size || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-400">Avg Deal</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SalesRepHome({ name }: { name: string }) {
  const navigate = useNavigate()

  const { data: tasksData } = useQuery({
    queryKey: ['tasks', { status: 'pending', limit: '5' }],
    queryFn: () => tasksApi.list({ status: 'pending', limit: '5', sort_by: 'due_date', sort_dir: 'asc' }),
  })

  const { data: overdueData } = useQuery({
    queryKey: ['tasks', 'overdue'],
    queryFn: tasksApi.overdue,
  })

  const { data: callsData } = useQuery({
    queryKey: ['voip-calls', { limit: '5' }],
    queryFn: () => voipApi.list({ limit: '5' }),
  })

  const pendingTasks = tasksData?.tasks || []
  const overdueCount = overdueData?.total || 0
  const recentCalls = callsData?.calls || []

  const quickLinks = [
    { label: 'My Leads', icon: Target, path: '/leads', color: 'bg-blue-500', desc: 'View and manage your assigned leads' },
    { label: 'Call Queue', icon: Phone, path: '/queue', color: 'bg-green-500', desc: 'Prioritized list of leads to call next' },
    { label: 'Tasks', icon: CheckSquare, path: '/tasks', color: 'bg-amber-500', desc: 'Follow-ups, demos, and pending actions' },
    { label: 'VoIP Calls', icon: PhoneCall, path: '/voip', color: 'bg-indigo-500', desc: 'Make and receive calls via browser' },
    { label: 'Companies', icon: Building2, path: '/companies', color: 'bg-purple-500', desc: 'Company profiles linked to leads' },
    { label: 'Email', icon: Mail, path: '/email', color: 'bg-pink-500', desc: 'Send emails from templates' },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, {name}</h1>
        <p className="text-sm text-gray-500 mt-0.5">Here's your daily overview</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CheckSquare} label="Pending Tasks" value={pendingTasks.length} color="bg-amber-500" />
        <StatCard icon={ClipboardList} label="Overdue" value={overdueCount} color={overdueCount > 0 ? 'bg-red-500' : 'bg-gray-400'} />
        <StatCard icon={Phone} label="Recent Calls" value={recentCalls.length} color="bg-green-500" />
        <StatCard icon={Zap} label="Call Queue" value="Go" sub="Start calling" color="bg-indigo-500" />
      </div>

      {/* Quick navigation */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Links</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickLinks.map((link) => (
            <button
              key={link.path}
              onClick={() => navigate(link.path)}
              className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-200 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${link.color} flex items-center justify-center flex-shrink-0`}>
                  <link.icon size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{link.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{link.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Help & tips */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle size={18} className="text-indigo-500" />
          <h2 className="text-lg font-semibold text-gray-900">Getting Started</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <HelpCard
            title="Working with Leads"
            items={[
              'View your assigned leads on the Leads page',
              'Click a lead to see full details, call history, and timeline',
              'Use the Call button to initiate a VoIP call directly',
              'Update lead stage as you progress through the pipeline',
            ]}
          />
          <HelpCard
            title="Making Calls"
            items={[
              'Use the Call Queue for a prioritized list of who to call next',
              'Click "Call Next" to auto-dial the highest priority lead',
              'VoIP calls work directly in your browser — no extra software needed',
              'After each call, rate the outcome for quality tracking',
            ]}
          />
          <HelpCard
            title="Managing Tasks"
            items={[
              'Tasks are auto-created for follow-ups and callbacks',
              'Set due dates to stay on top of your pipeline',
              'Complete tasks by clicking the circle icon',
              'Overdue tasks are highlighted in red',
            ]}
          />
          <HelpCard
            title="Tips for Success"
            items={[
              'HOT leads (red badge) should be contacted within 30 minutes',
              'Use the lead timeline to review past interactions before calling',
              'Add notes after every call for better context next time',
              'Check the Call Queue daily — it prioritizes by SLA urgency',
            ]}
          />
        </div>
      </div>
    </div>
  )
}

function MarketingHome({ name }: { name: string }) {
  const navigate = useNavigate()

  const quickLinks = [
    { label: 'Blog Writer', icon: ClipboardList, path: '/tools/blog', color: 'bg-blue-500', desc: 'Generate SEO-optimized blog posts' },
    { label: 'Social Posts', icon: Users, path: '/tools/social', color: 'bg-pink-500', desc: 'Create social media content' },
    { label: 'Campaign Planner', icon: Target, path: '/tools/campaign', color: 'bg-purple-500', desc: 'Plan marketing campaigns' },
    { label: 'Strategy', icon: TrendingUp, path: '/tools/strategy', color: 'bg-indigo-500', desc: 'PRDs, market research, GTM plans' },
    { label: 'Meta Ads', icon: DollarSign, path: '/meta', color: 'bg-emerald-500', desc: 'Manage Facebook & Instagram ads' },
    { label: 'Social Analytics', icon: ListChecks, path: '/social-analytics', color: 'bg-amber-500', desc: 'Cross-platform social performance' },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{getGreeting()}, {name}</h1>
        <p className="text-sm text-gray-500 mt-0.5">Your marketing toolkit</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {quickLinks.map((link) => (
          <button
            key={link.path}
            onClick={() => navigate(link.path)}
            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-200 hover:shadow-sm transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${link.color} flex items-center justify-center flex-shrink-0`}>
                <link.icon size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{link.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{link.desc}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function HelpCard({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-2">{title}</h3>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
            <span className="w-1 h-1 bg-indigo-400 rounded-full mt-1.5 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

function BackfillButton() {
  const queryClient = useQueryClient()
  const [result, setResult] = useState<any>(null)
  const mutation = useMutation({
    mutationFn: () => reportsApi.backfillVoipCosts(),
    onSuccess: (data) => {
      setResult(data.data)
      queryClient.invalidateQueries({ queryKey: ['voip-costs'] })
    },
  })

  return (
    <div className="flex items-center gap-2">
      {result && (
        <span className="text-xs text-green-600">
          {result.captured} captured, {result.pending} pending, {result.failed} failed
        </span>
      )}
      {mutation.isError && (
        <span className="text-xs text-red-600">{(mutation.error as any)?.message || 'Failed'}</span>
      )}
      <button
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 font-medium px-2 py-1 rounded border border-gray-200 hover:border-indigo-300 transition-colors disabled:opacity-50"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        <RefreshCw size={12} className={mutation.isPending ? 'animate-spin' : ''} />
        {mutation.isPending ? 'Syncing...' : 'Backfill Costs'}
      </button>
    </div>
  )
}
